import { FC, useEffect, useState } from "react";
import { ExportBillingData } from "../../../../../../api-client";
import useStore from "../../../../../../helpers/useStore";
import { useMasterTables } from "../../../../../../stores/MasterTables";
import notificationStore from "../../../../../../stores/Notification/notificationStore";
import { Box, Grid } from "@mui/material";
import BillBoxWrapper from "../../../DirectBilling/common/components/BillBoxWrapper";
import { TopLevelPaper } from "../../../../../../themes/StyledComponents";
import CopaymentEditBillInfo from "../components/CopaymentEditBillInfo";
import TransactionRxOverview from "../../../Base/components/TransactionRxOverview";

interface EditCopaymentFullScreenProps {
  handleFullScreenDialogClose: (skipWarning?: boolean) => void;
  initialBills: ExportBillingData[]; // Pass the bills data as a prop
}

/**
 * The parent component is the "source of truth" for the bills state.
 */
const EditCopaymentFullScreen: FC<EditCopaymentFullScreenProps> = ({
  handleFullScreenDialogClose,
  initialBills,
}) => {
  const [bills, setBills] = useState<ExportBillingData[]>(initialBills);

  const [selectedRx, setSelectedRx] = useState<any>(null);
  const [isVisible, setIsVisible] = useState(false);

  const { data: masterTablesData } = useMasterTables();
  const [editRxMode, setEditRxMode] = useState<boolean>(false);

  /**
   * Called by the child (EditBillInfo) when it deletes an Rx.
   * If the currently selected Rx is the same as the deleted one, clear it.
   */
  const handleRxDelete = (deletedRxId: string) => {
    if (selectedRx && selectedRx.id === deletedRxId) {
      setSelectedRx(null);
      setIsVisible(false);
    }
  };

  const handleRemoveAll = (billId: string) => {
    // Find which bill is being removed
    const targetBill = bills.find((b) => b.billId === billId);
    if (!targetBill) return;

    // If the currently selected Rx is from the bill being removed, un-select it
    if (selectedRx && targetBill.data.rxs.some((rx) => rx.id === selectedRx.id)) {
      setSelectedRx(null);
      setIsVisible(false);
    }

    setBills((prevBills) => prevBills.filter((b) => b.billId !== billId));
  };

  /**
   * Called by the child when it filters out invalid (aka not all green ticks in Validierung) Rxs.
   * If the currently selected Rx isn't in the remaining list, hide the details panel.
   */
  const handleValidationFilter = (remainingRxs: any[]) => {
    if (selectedRx && !remainingRxs.some((rx) => rx.id === selectedRx.id)) {
      setSelectedRx(null);
      setIsVisible(false);
    }
  };

  /**
   * When a user clicks on a row in the table, we figure out which bill contains that Rx
   * and show the details in BillOverview + TerminView.
   */
  const handleRowClick = (rx: any) => {
    // Parent uses *its* version of "bills" (source of truth)
    const bill = bills.find((b) => b.data.rxs.some((r) => r.id === rx.id));

    if (!bill) {
      notificationStore.showMessage("Zu diesem Rezept wurde keine Rechnung gefunden", "error");
      return;
    }

    // Attach accountInfo to the selected Rx
    const selectedRxWithAccount = { ...rx, account: bill.accountInfo };

    setSelectedRx(selectedRxWithAccount);
    setIsVisible(true);
  };

  useEffect(() => {}, [bills]);

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Grid
        container
        spacing={1}
        sx={{ height: "100%", marginTop: "1px" }}
      >
        <BillBoxWrapper xs={5}>
          <TopLevelPaper
            className="scrollable"
            sx={{ paddingTop: 0 }}
          >
            <CopaymentEditBillInfo
              handleFullScreenDialogClose={handleFullScreenDialogClose}
              onRowClick={handleRowClick}
              onRxDelete={handleRxDelete}
              bills={bills} // Pass the bills data here
              setBills={setBills}
              onValidationFilter={handleValidationFilter}
              onRemoveAll={handleRemoveAll}
            />
          </TopLevelPaper>
        </BillBoxWrapper>
        {isVisible && (
          <>
            <BillBoxWrapper xs={7}>
              <TopLevelPaper className="scrollable">
                <TransactionRxOverview
                  rowData={selectedRx}
                  masterData={masterTablesData}
                  editRxMode={editRxMode}
                  setEditRxMode={setEditRxMode}
                />
              </TopLevelPaper>
            </BillBoxWrapper>
          </>
        )}
      </Grid>
    </Box>
  );
};

export default EditCopaymentFullScreen;
